/**
 * billingService — customer-facing subscription status (best-effort).
 *
 * Talks to the 'billing-webhook' Cloudflare Worker:
 *   GET  {billingURL}/billing/status   → subscription status JSON
 *   POST {billingURL}/billing/portal   → { url } Stripe Billing Portal session
 *   POST {billingURL}/billing/checkout → { url } Stripe Checkout session (new;
 *                                         may 404 on a not-yet-deployed worker)
 *
 * CRITICAL INVARIANT: billing status is best-effort. On ANY failure (network,
 * timeout, 4xx/5xx, malformed payload) fetchBillingStatus() resolves to null
 * and the webapp must behave exactly as it does today. Never throw from here
 * into a page render path, and never let a billing hiccup blank the dashboard
 * (see "Device Data Loading & Resilience" in CLAUDE.md for the history).
 *
 * Demo mode must NEVER call the endpoint — pass app.state.isDemo in and the
 * service returns null immediately.
 */

import axios from 'axios';
import Configuration from '../constant/configuration';
import { getToken } from './tokenStorage';
import errorLogger from '../utils/errorLogger';

// ── Constants ─────────────────────────────────────────────────────────────────

const BILLING_REQUEST_TIMEOUT_MS = 15000; // never hang a page on billing
const BILLING_CACHE_TTL_MS = 5 * 60 * 1000; // 5-minute in-memory cache

// checkout_kinds is new on /billing/status and may be MISSING or [] on a
// worker deploy that predates it. Anything outside this set is dropped by
// getCheckoutKinds() rather than trusted verbatim (never trust external data).
const VALID_CHECKOUT_KINDS = ['monthly', 'annual', 'lifetime'];

// ── Internal state ────────────────────────────────────────────────────────────

// Every entry is keyed by the session token it was fetched with: a different
// token (another account signing in in the same tab) is a miss, and never
// joins the previous account's in-flight request (audit 2026-10-15, M5).
let _cachedStatus = null; // last successfully fetched status object
let _cachedToken = null; // session token _cachedStatus was fetched with
let _cachedAtMs = 0; // epoch ms of the last successful fetch
let _inFlight = null; // single in-flight request promise (dedupe)
let _inFlightToken = null; // session token _inFlight was sent with
// Bumped by invalidate(): a request that started before an invalidate (sign-
// out, pause, resume) must not write its now-stale answer into the cache.
let _generation = 0;

// ── Helpers ───────────────────────────────────────────────────────────────────

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

/**
 * Minimal shape validation — never trust external data. A payload without a
 * string service_state is treated as "billing unavailable" (null).
 */
function isValidStatus(data) {
  return !!data && typeof data === 'object' && typeof data.service_state === 'string';
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Fetch the current billing status. Best-effort: resolves to null on any
 * error so callers can simply hide billing UI when the endpoint is down.
 *
 * @param {number|boolean} isDemo  app.state.isDemo — when truthy, no request
 *                                 is made and null is returned immediately.
 * @returns {Promise<object|null>} status object or null
 */
export function fetchBillingStatus(isDemo) {
  if (isDemo) return Promise.resolve(null);

  const token = getToken();
  if (!token) return Promise.resolve(null);

  const now = Date.now();
  if (_cachedStatus && _cachedToken === token && now - _cachedAtMs < BILLING_CACHE_TTL_MS) {
    return Promise.resolve(_cachedStatus);
  }

  if (_inFlight && _inFlightToken === token) return _inFlight;

  const generation = _generation;
  const request = axios
    .get(`${Configuration.billingURL}/billing/status`, {
      headers: authHeaders(token),
      timeout: BILLING_REQUEST_TIMEOUT_MS,
    })
    .then((res) => {
      const data = res && res.data;
      if (!isValidStatus(data)) {
        errorLogger.logError('BillingStatusInvalidPayload', 'Unexpected /billing/status payload shape');
        return null;
      }
      // Cache only while this is still the same session and nothing has
      // invalidated since the request started; the caller still gets its
      // own answer either way.
      if (generation === _generation && getToken() === token) {
        _cachedStatus = data;
        _cachedToken = token;
        _cachedAtMs = Date.now();
      }
      return data;
    })
    .catch((err) => {
      // Best-effort: log and degrade to "no billing info". Errors are NOT
      // cached, so a later call retries once the TTL/in-flight gate allows.
      errorLogger.logError('BillingStatusFetchFailed', err);
      return null;
    })
    .finally(() => {
      // Only release the slot this request owns: another account's request
      // may have replaced it while this one was in flight.
      if (_inFlight === request) {
        _inFlight = null;
        _inFlightToken = null;
      }
    });

  _inFlight = request;
  _inFlightToken = token;
  return request;
}

/**
 * fetchBillingStatus for a page, but only once the /customer record has
 * resolved (app.state.customerLoaded, set by GlobalAccess.getDevices). Before
 * that, app.state.isDemo is still its boot default 0 on a cold load, so a
 * mount-time call sent the public demo account to the billing worker (a 403
 * on every page). Returns null — nothing requested — while the customer is
 * unknown; callers retry from componentDidUpdate.
 *
 * @param {object} app  The App instance (reads app.state).
 * @returns {Promise<object|null>|null}
 */
export function fetchBillingStatusForApp(app) {
  const state = (app && app.state) || {};
  if (!state.customerLoaded) return null;
  return fetchBillingStatus(state.isDemo);
}

/**
 * Request a Stripe Billing Portal session URL.
 * Resolves to the portal URL string, or null on any error (callers should
 * show a friendly toast and stay on the page).
 *
 * @param {number|boolean} isDemo  app.state.isDemo — blocked in demo mode.
 * @returns {Promise<string|null>}
 */
export async function requestPortalSession(isDemo) {
  if (isDemo) return null;

  const token = getToken();
  if (!token) return null;

  try {
    const res = await axios.post(
      `${Configuration.billingURL}/billing/portal`,
      {},
      { headers: authHeaders(token), timeout: BILLING_REQUEST_TIMEOUT_MS }
    );
    const url = res && res.data && res.data.url;
    return typeof url === 'string' && url ? url : null;
  } catch (err) {
    errorLogger.logError('BillingPortalRequestFailed', err);
    return null;
  }
}

/**
 * Pause billing on the caller's own subscription.
 *
 * Stripe removed customer pause/resume from the billing portal on 2024-03-29,
 * so "Manage subscription" cannot offer this — the worker's /billing/pause is
 * the only route. Exists for seasonal customers (mobile bars, event caterers)
 * whose alternative was cancelling outright over a closed season.
 *
 * Pausing GATES the dashboard until the customer resumes, so callers must
 * confirm intent before calling this — it is not an undoable toggle.
 *
 * Resolves true only on a confirmed pause. Every failure resolves false so the
 * caller can show a friendly message and stay on the page, matching the
 * best-effort contract of the rest of this module.
 *
 * @param {number|boolean} isDemo  app.state.isDemo — blocked in demo mode.
 * @returns {Promise<boolean>}
 */
export async function pauseSubscription(isDemo) {
  if (isDemo) return false;

  const token = getToken();
  if (!token) return false;

  try {
    const res = await axios.post(
      `${Configuration.billingURL}/billing/pause`,
      {},
      { headers: authHeaders(token), timeout: BILLING_REQUEST_TIMEOUT_MS }
    );
    if (res && res.data && res.data.ok === true) {
      invalidate(); // the status cache still says active
      return true;
    }
    return false;
  } catch (err) {
    // 409 no_active_subscription means there is nothing left to pause — the
    // plan is ALREADY paused (the Pause control only renders while a plan is
    // active, so reaching this means the state changed underneath the page).
    // Reporting "could not pause, try again" told a paused customer the
    // opposite of the truth and invited them to keep retrying.
    const data = err && err.response && err.response.data;
    if (err && err.response && err.response.status === 409 && data && data.error === 'no_active_subscription') {
      invalidate();
      return true;
    }
    errorLogger.logError('BillingPauseFailed', err);
    return false;
  }
}

/**
 * Resume a paused subscription.
 *
 * Stripe removed customer pause/resume from the billing portal on 2024-03-29,
 * so the portal cannot do this — without this call the paused interstitial
 * would show a Resume button that leads nowhere.
 *
 * Resolves true only on a confirmed resume. Every failure resolves false so
 * the caller can show a friendly message and stay on the page, matching the
 * best-effort contract of the rest of this module.
 *
 * @param {number|boolean} isDemo  app.state.isDemo — blocked in demo mode.
 * @returns {Promise<boolean>}
 */
export async function resumeSubscription(isDemo) {
  if (isDemo) return false;

  const token = getToken();
  if (!token) return false;

  try {
    const res = await axios.post(
      `${Configuration.billingURL}/billing/resume`,
      {},
      { headers: authHeaders(token), timeout: BILLING_REQUEST_TIMEOUT_MS }
    );
    if (res && res.data && res.data.ok === true) {
      invalidate(); // the status cache still says paused
      return true;
    }
    return false;
  } catch (err) {
    errorLogger.logError('BillingResumeFailed', err);
    return false;
  }
}

/**
 * Request a Stripe Checkout session URL for a NEW subscription/purchase
 * (adding a plan or more ports). Mirrors requestPortalSession exactly:
 * resolves to the checkout URL string, or null on ANY error — including a
 * 404 from a worker deploy that doesn't have this endpoint yet, so callers
 * should treat that identically to any other best-effort failure (friendly
 * toast, stay on the page).
 *
 * @param {number|boolean} isDemo    app.state.isDemo — blocked in demo mode.
 * @param {string} kind              'monthly' | 'annual' | 'lifetime'
 * @param {number} quantity          number of ports to purchase
 * @returns {Promise<string|null>}
 */
export async function requestCheckoutSession(isDemo, kind, quantity) {
  if (isDemo) return null;

  const token = getToken();
  if (!token) return null;

  try {
    const res = await axios.post(
      `${Configuration.billingURL}/billing/checkout`,
      { kind, quantity },
      { headers: authHeaders(token), timeout: BILLING_REQUEST_TIMEOUT_MS }
    );
    const url = res && res.data && res.data.url;
    return typeof url === 'string' && url ? url : null;
  } catch (err) {
    errorLogger.logError('BillingCheckoutRequestFailed', err);
    return null;
  }
}

/**
 * Build the Stripe Checkout subscribe URL for an unsubscribed org.
 * Returns null when the status has no subscribe_url / org_id.
 *
 * @param {object|null} status  A status object from fetchBillingStatus().
 * @returns {string|null}
 */
export function getSubscribeUrl(status) {
  if (!status || !status.subscribe_url || !status.org_id) return null;
  return `${status.subscribe_url}?client_reference_id=${encodeURIComponent(status.org_id)}`;
}

/**
 * Extract the valid checkout kinds from a status object. checkout_kinds is
 * new on /billing/status and may be MISSING or [] on a worker deploy that
 * predates it — both resolve to [] here so callers fall back to the legacy
 * subscribe_url link (see getSubscribeUrl). Any entries outside the known
 * kind set are dropped rather than trusted (never trust external data).
 *
 * @param {object|null} status  A status object from fetchBillingStatus().
 * @returns {string[]}          Subset of ['monthly', 'annual', 'lifetime'].
 */
export function getCheckoutKinds(status) {
  if (!status || !Array.isArray(status.checkout_kinds)) return [];
  return status.checkout_kinds.filter((k) => VALID_CHECKOUT_KINDS.indexOf(k) !== -1);
}

/**
 * Format an ISO date string for display in the viewer's locale.
 * Returns null for missing/invalid input so callers can hide the field.
 *
 * @param {string|null} iso     ISO 8601 timestamp (e.g. next_renewal).
 * @param {string} [locale]     BCP-47 locale (e.g. i18n.language).
 * @returns {string|null}
 */
export function formatBillingDate(iso, locale) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  try {
    return d.toLocaleDateString(locale || undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return d.toLocaleDateString();
  }
}

/**
 * Invalidate the in-memory status cache (e.g. after returning from the
 * billing portal or checkout, so the next fetch reflects the new state, and
 * on sign-out via services/sessionReset.js). A request already in flight
 * still answers its caller but no longer writes the cache.
 */
export function invalidate() {
  _generation++;
  _cachedStatus = null;
  _cachedToken = null;
  _cachedAtMs = 0;
  _inFlight = null;
  _inFlightToken = null;
}
